import React, { useRef, useEffect, useState, useMemo } from 'react'
import styled from 'styled-components'
import CommandInput from './InputCommands'
import { useConfig } from '@/contexts/ConfigContext'
import { ButtonBorderLight } from '@components/Form/ButtonLight'
import { hueAnimationBackground } from '@/components/Containers/StyledCotainers'
import { useLanguage } from '@/contexts/LanguageContext'
import { useUI } from '@/contexts/UIContext'
import { decodeCrosshairShareCode, crosshairToConVars, parseCrosshairCommands, normalizeValue } from './Crosshair.utils'
import { Tooltip } from '@mui/material'
import { CROSSHAIR_GALLERY, RECOMMENDED_GALLERY } from '@/constants/crosshairGallery'
import CrosshairMiniPreview from './CrosshairMiniPreview'
import { Bookmarks as BookmarksIcon, Add as AddIcon, Delete as DeleteIcon, Save as SaveIcon, PlaylistAdd as PlaylistAddIcon, Verified as VerifiedIcon, Stars as StarsIcon, Edit as EditIcon, Check as CheckIcon, Close as CloseIcon } from '@mui/icons-material'
import { HexColorPicker } from 'react-colorful'
import Modal from '@/components/Modal/Modal'
import { track } from '@/utils/analytics'

import KeySelector from '@/components/CustomInput/KeySelector'
// ------- STYLES -------
const LayoutContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 10px;
`

const Divider = styled.div`
    height: 1px;
    width: 100%;
    background: linear-gradient(90deg, 
        rgba(99, 102, 241, 0) 0%, 
        rgba(99, 102, 241, 0.15) 20%, 
        rgba(99, 102, 241, 0.15) 80%, 
        rgba(99, 102, 241, 0) 100%
    );
    margin: 15px 0;
    opacity: 0.8;
`

const MainRow = styled.div`
    display: flex;
    gap: 15px;
    justify-content: center;
    flex-wrap: wrap;
`

const PreviewBox = styled.div`
    position: relative;
    width: 100%;
    max-width: 530px;
    height: 310px;
    flex-shrink: 0;
    margin-top: 0px;
    background: linear-gradient(
        180deg,
        rgba(15, 23, 42, 0.6) 0%,
        rgba(15, 23, 42, 0.95) 100%
    );
    border: 1px solid rgba(99, 102, 241, 0.2);
    border-radius: 12px;
    overflow: hidden;
    box-shadow: inset 0px 0px 100px #05020879;
    z-index: 105;

`

const TabsHeader = styled.div`
    display: flex;
    gap: 4px;
    background: rgba(0, 0, 0, 0.2);
    padding: 0;
    border-radius: 8px;
    width: fit-content;
`

const MainTabButton = styled.button`
    background: ${({ active }) => active ? 'rgba(99, 102, 241, 0.15)' : 'transparent'};
    color: ${({ active }) => active ? '#fff' : 'rgba(255, 255, 255, 0.6)'};
    border: none;
    border-bottom: 2px solid ${({ active }) => active ? '#6366f1' : 'transparent'};
    padding: 10px 20px;
    border-radius: 0;
    font-size: 0.8rem;
    font-weight: 600;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    font-family: var(--font-chakra);
    letter-spacing: 1px;
    text-transform: uppercase;
    opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
    pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

    &:hover {
        color: #fff;
        background: rgba(99, 102, 241, 0.08);
        border-bottom-color: ${({ active }) => active ? '#6366f1' : 'rgba(99, 102, 241, 0.4)'};
    }
`

const ModeButton = styled.button`
    background: ${({ active }) => active ? 'rgba(255, 179, 0, 0.15)' : 'rgba(255, 179, 0, 0.05)'};
    color: ${({ active }) => active ? '#ffb300' : 'rgba(255, 255, 255, 0.5)'};
    border: 1px solid ${({ active }) => active ? 'rgba(255, 179, 0, 0.4)' : 'rgba(255, 179, 0, 0.1)'};
    padding: 6px 14px;
    border-radius: 20px;
    font-size: 0.7rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    font-family: var(--font-roboto);
    display: flex;
    align-items: center;
    gap: 6px;

    &:hover {
        background: ${({ active }) => active ? 'rgba(255, 179, 0, 0.2)' : 'rgba(255, 255, 255, 0.05)'};
        color: ${({ active }) => active ? '#ffca28' : '#fff'};
    }

    ${({ active }) => active && `
        box-shadow: 0 0 10px rgba(255, 179, 0, 0.1);
    `}
`

const ActionButton = styled.button`
    background: rgba(99, 102, 241, 0.15);
    color: #a5b4fc;
    border: 1px solid rgba(99, 102, 241, 0.3);
    padding: 6px 18px;
    border-radius: 20px;
    font-size: 0.65rem;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    font-family: var(--font-chakra);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    display: flex;
    align-items: center;
    gap: 8px;
    box-shadow: none;
    position: relative;
    overflow: hidden;

    &::after {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
        transform: translateX(-100%);
        transition: transform 0.6s;
    }

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(99, 102, 241, 0.5);
        background: linear-gradient(135deg, #6366f1 0%, #4338ca 100%);
        color: #fff;
        border-color: transparent;

        &::after {
            transform: translateX(100%);
        }

        svg {
            transform: rotate(180deg);
        }
    }

    &:active {
        transform: translateY(0);
        box-shadow: 0 2px 10px rgba(99, 102, 241, 0.4);
    }

    svg {
        transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        color: inherit;
    }
`

const GridLayer = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image:
        linear-gradient(rgba(99, 102, 241, 0.05) 1px, transparent 1px),
        linear-gradient(90deg, rgba(99, 102, 241, 0.05) 1px, transparent 1px);
    background-size: 20px 20px;
    background-position: center;
    z-index: 2;
    pointer-events: none;
`

const BackgroundLayer = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: ${({ color }) => color};
    z-index: 1;
    transition: background-color 0.3s ease;
`

const Canvas = styled.canvas`
    width: 100%;
    height: 100%;
    image-rendering: pixelated;
    position: relative;
    z-index: 5;
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    
    height: 100%;
    width: 100%;
    margin: 0 auto;
    display: block;

    transform: scale(${({ $scale }) => $scale});
    transform-origin: center center;
`

const FloatingTogglesWrapper = styled.div`
    position: absolute;
    top: 15px;
    right: 15px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    z-index: 30;
`

// Obsolete wrappers removed


const ZoomActionBtn = styled.div`
    height: 28px;
    padding: 0 12px;
    border-radius: 8px;
    background: ${({ active }) =>
        active ? 'rgba(99, 102, 241, 0.4)' : 'rgba(0, 0, 0, 0.3)'};
    border: 1px solid
        ${({ active }) =>
        active ? 'rgba(99, 102, 241, 0.6)' : 'rgba(255, 255, 255, 0.1)'};
    color: ${({ active }) => (active ? '#fff' : 'rgba(255, 255, 255, 0.5)')};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    backdrop-filter: blur(4px);
    font-size: 0.6rem;
    font-weight: 400;
    font-family: var(--font-roboto);
    white-space: nowrap;

    &:hover {
        background: ${({ active }) => active ? 'rgba(99, 102, 241, 0.5)' : 'rgba(255, 255, 255, 0.1)'};
        border-color: ${({ active }) => active ? '#6366f1' : 'rgba(255, 255, 255, 0.3)'};
        color: #fff;
    }

    ${({ active }) =>
        active &&
        `
        box-shadow: 0 0 15px rgba(99, 102, 241, 0.3);
    `}
`

const ToggleBtn = styled.div`
    width: 28px;
    height: 28px;
    border-radius: 8px;
    background: ${({ active }) =>
        active ? 'rgba(99, 102, 241, 0.4)' : 'rgba(0, 0, 0, 0.3)'};
    border: 1px solid
        ${({ active }) =>
        active ? 'rgba(99, 102, 241, 0.6)' : 'rgba(255, 255, 255, 0.1)'};
    color: ${({ active }) => (active ? '#fff' : 'rgba(255, 255, 255, 0.5)')};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    backdrop-filter: blur(4px);
    font-size: 0.6rem;
    font-weight: 400;
    font-family: var(--font-roboto);
    opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
    pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};

    &:hover {
        background: ${({ active }) => active ? 'rgba(99, 102, 241, 0.5)' : 'rgba(255, 255, 255, 0.1)'};
        border-color: ${({ active }) => active ? '#6366f1' : 'rgba(255, 255, 255, 0.3)'};
        color: #fff;
    }

    ${({ active }) =>
        active &&
        `
        box-shadow: 0 0 15px rgba(99, 102, 241, 0.3);
    `}
`

const FloatingActions = styled.div`
    position: absolute;
    top: 15px;
    left: 15px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    z-index: 30;
`

const StyledIconButton = styled.div`
    width: 28px;
    height: 28px;
    border-radius: 8px;
    background: ${({ active }) =>
        active ? 'rgba(99, 102, 241, 0.4)' : 'rgba(0, 0, 0, 0.3)'};
    border: 1px solid
        ${({ active }) =>
        active ? 'rgba(99, 102, 241, 0.6)' : 'rgba(255, 255, 255, 0.1)'};
    color: ${({ active }) => (active ? '#fff' : 'rgba(255, 255, 255, 0.5)')};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    backdrop-filter: blur(4px);
    opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
    pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};

    &:hover {
        background: ${({ active }) => active ? 'rgba(99, 102, 241, 0.5)' : 'rgba(255, 255, 255, 0.1)'};
        border-color: ${({ active }) => active ? '#6366f1' : 'rgba(255, 255, 255, 0.3)'};
        color: #fff;
    }

    ${({ active }) =>
        active &&
        `
        box-shadow: 0 0 15px rgba(99, 102, 241, 0.3);
    `}
`

// --- Gallery Styled Components ---
const GalleryGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, 130px);
    gap: 17px;
    width: 100%;
    margin-top: 6px;
    max-height: 400px;
    padding-right: 4px;
    justify-content: flex-start;

    &::-webkit-scrollbar {
        width: 4px;
    }
    &::-webkit-scrollbar-thumb {
        background: rgba(99, 102, 241, 0.3);
        border-radius: 2px;
    }
`

const GalleryControls = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    gap: 15px;
`

const ImportContainer = styled.div`
    display: flex;
    gap: 20px;
    padding: 20px 0;
    width: 100%;
    justify-content: center;
`

const ExportContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 20px 0;
    width: 100%;
    max-width: 800px;
    margin: 0 auto;
`

const ExportGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
`

const ExportCard = styled.div`
    background: rgba(15, 23, 42, 0.4);
    border: 1px solid rgba(255, 255, 255, 0.05);
    border-radius: 12px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 12px;
`

const StyledPopover = styled.div`
    position: fixed;
    top: 15%;
    left: 47%;
    transform: translateX(-50%);
    z-index: 2000;
    padding: 20px;
    background: #1e2025;
    border-radius: 20px;
    box-shadow: 0 25px 60px rgba(0,0,0,0.9), 0 0 0 1px rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.1);
    
    animation: popIn 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
    
    @keyframes popIn {
        from { opacity: 0; transform: translateX(-50%) scale(0.9) translateY(-30px); }
        to { opacity: 1; transform: translateX(-50%) scale(1) translateY(0); }
    }

    

    .react-colorful {
        .react-colorful__saturation {
            width: 100%;
            height: 100%;
        }

        .react-colorful__hue {
            width: 100%;
        }
    }
`

const PopoverBackdrop = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 1500;
    cursor: default;
    background: rgba(0, 0, 0, 0.3);
    backdrop-filter: blur(1px);
`

const ImportCard = styled.div`
    flex: 1;
    max-width: 320px;
    background: linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(15, 23, 42, 0.4) 100%);
    border: 1px solid rgba(99, 102, 241, 0.15);
    border-radius: 12px;
    padding: 24px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;

    &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(45deg, transparent 0%, rgba(99, 102, 241, 0.1) 100%);
        opacity: 0;
        transition: opacity 0.3s;
        pointer-events: none;
    }

    &:hover {
        transform: translateY(-5px);
        border-color: rgba(99, 102, 241, 0.4);
        box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3), 0 0 20px rgba(99, 102, 241, 0.1);
        
        &::before {
            opacity: 1;
        }

        .import-icon {
            transform: scale(1.1) rotate(5deg);
            color: #6366f1;
        }

        h4 {
            color: #fff;
        }
    }

    h4 {
        margin: 0;
        font-size: 1rem;
        font-family: var(--font-chakra);
        color: rgba(255, 255, 255, 0.8);
        text-transform: uppercase;
        letter-spacing: 1px;
        transition: color 0.2s;
    }

    p {
        margin: 0;
        font-size: 0.75rem;
        color: rgba(255, 255, 255, 0.4);
        text-align: center;
        line-height: 1.5;
    }
`

const ImportIcon = styled.div`
    width: 44px;
    height: 44px;
    border-radius: 10px;
    background: rgba(99, 102, 241, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    color: rgba(99, 102, 241, 0.6);
    transition: all 0.3s;
    margin-bottom: 4px;
`

const ImportInput = styled.input`
    background: rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 6px;
    padding: 8px 12px;
    color: #fff;
    font-size: 0.75rem;
    font-family: var(--font-roboto);
    width: 100%;
    margin-top: 10px;
    transition: all 0.2s;

    &:focus {
        outline: none;
        border-color: rgba(99, 102, 241, 0.5);
        background: rgba(0, 0, 0, 0.4);
    }
`

const ImportTextarea = styled.textarea`
    background: rgba(0, 0, 0, 0.3);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 6px;
    padding: 8px 12px;
    color: #fff;
    font-size: 0.7rem;
    font-family: var(--font-roboto);
    width: 100%;
    height: 60px;
    margin-top: 10px;
    transition: all 0.2s;
    resize: none;

    &:focus {
        outline: none;
        border-color: rgba(99, 102, 241, 0.5);
        background: rgba(0, 0, 0, 0.4);
    }
`

const ImportActionButton = styled.button`
    background: ${({ $variant }) => $variant === 'success' ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : 'linear-gradient(135deg, #6366f1 0%, #4338ca 100%)'};
    color: #fff;
    border: none;
    padding: 6px 16px;
    border-radius: 6px;
    font-size: 0.7rem;
    font-weight: 700;
    cursor: pointer;
    margin-top: 12px;
    width: 100%;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    transition: all 0.2s;

    &:hover {
        transform: translateY(-2px);
        filter: brightness(1.1);
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        transform: none;
    }
`

const SearchInput = styled.input`
    background: rgba(0, 0, 0, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 6px;
    padding: 6px 12px;
    color: #fff;
    font-size: 0.75rem;
    font-family: var(--font-roboto);
    width: 200px;
    transition: all 0.2s;

    &:focus {
        outline: none;
        border-color: rgba(99, 102, 241, 0.5);
        background: rgba(0, 0, 0, 0.3);
    }

    &::placeholder {
        color: rgba(255, 255, 255, 0.3);
    }
`

const GalleryPagination = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`

const PaginationCircle = styled.button`
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: ${({ disabled }) => disabled ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.08)'};
    border: 1px solid ${({ disabled }) => disabled ? 'transparent' : 'rgba(255, 255, 255, 0.1)'};
    color: ${({ disabled }) => disabled ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.6)'};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
    transition: all 0.2s;
    padding: 0;

    &:hover {
        background: ${({ disabled }) => disabled ? 'rgba(255, 255, 255, 0.03)' : 'rgba(255, 255, 255, 0.15)'};
        color: #fff;
        border-color: rgba(255, 255, 255, 0.2);
        transform: ${({ disabled }) => disabled ? 'none' : 'scale(1.05)'};
    }

    &:active {
        transform: scale(0.95);
    }
`

const GalleryCard = styled.div`
    position: relative;
    width: 130px;
    height: 130px;
    border-radius: 8px;
    overflow: hidden;
    //background: rgba(15, 23, 42, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.1);
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    display: flex;
    flex-direction: column;
    opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
    pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

    &:hover {
        transform: translateY(-2px);
        border-color: rgba(99, 102, 241, 0.6);
        box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
        
        .gallery-info {
            background: rgba(15, 23, 42, 0.95);
        }
    }

    ${({ active }) => active && `
        border-color: #6366f1;
        box-shadow: 0 0 0 1px #6366f1;
    `}

    ${({ $isDeleting }) => $isDeleting && `
        border-color: #ef4444 !important;
        box-shadow: 0 0 20px rgba(239, 68, 68, 0.5) !important;
        transform: scale(0.8) !important;
        opacity: 0 !important;
        transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1) !important;
        pointer-events: none;
        
        &::before {
            content: '';
            position: absolute;
            top: 0; left: 0; width: 100%; height: 100%;
            background: rgba(239, 68, 68, 0.4);
            z-index: 20;
            animation: flashRed 0.2s ease-out forwards;
        }

        @keyframes flashRed {
            from { opacity: 0; }
            to { opacity: 1; }
        }
    `}
`

const GalleryInfo = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 2px;
    transition: background 0.2s;
`

const GalleryName = styled.span`
    font-size: 0.75rem;
    font-weight: 700;
    color: #fff;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`

const GalleryPlayer = styled.span`
    font-size: 0.65rem;
    color: rgba(255, 255, 255, 0.8);
    display: flex;
    align-items: center;
    gap: 4px;

    &::before {
        content: '';
        display: block;
        width: 4px;
        height: 4px;
        border-radius: 50%;
        background: #ff4d94;
        box-shadow: 0 0 6px #ff4d94;
    }
`

// --- Custom Dropdown Components ---
const DropdownContainer = styled.div`
    position: relative;
    width: 200px;
    font-family: var(--font-roboto);
    z-index: 60;
`
const DropdownHeader = styled.div`
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    background: rgba(15, 23, 42, 0.9);
    border: 1px solid rgba(99, 102, 241, 0.4);
    border-radius: 8px;
    color: #eee;
    font-size: 0.75rem;
    font-weight: 400;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    backdrop-filter: blur(8px);
    transition: all 0.2s ease;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
    opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
    pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

    &:hover {
        border-color: ${({ disabled }) => (disabled ? 'rgba(99, 102, 241, 0.4)' : 'rgba(99, 102, 241, 1)')};
        background: ${({ disabled }) => (disabled ? 'rgba(15, 23, 42, 0.9)' : 'rgba(20, 30, 50, 1)')};
        color: ${({ disabled }) => (disabled ? '#eee' : '#fff')};
        box-shadow: ${({ disabled }) => (disabled ? 'none' : '0 0 20px rgba(99, 102, 241, 0.25)')};
    }
`
const DropdownList = styled.div`
    position: absolute;
    top: calc(100% + 8px); /* Open downwards */
    left: 0;
    width: 100%;
    background: rgba(15, 23, 42, 0.98);
    border: 1px solid rgba(99, 102, 241, 0.4);
    border-radius: 8px;
    overflow: hidden;
    display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.8);
    backdrop-filter: blur(12px);
    animation: slideDownFade 0.2s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: 100;

    @keyframes slideDownFade {
        from { opacity: 0; transform: translateY(-10px); }
        to { opacity: 1; transform: translateY(0); }
    }
`
const DropdownItem = styled.div`
    padding: 8px 10px;
    font-size: 0.65rem;
    font-weight: 400;
    color: ${({ active }) => (active ? '#fff' : 'rgba(255, 255, 255, 0.6)')};
    background: ${({ active }) => (active ? 'rgba(99, 102, 241, 0.25)' : 'transparent')};
    cursor: pointer;
    transition: all 0.15s;
    border-left: 3px solid ${({ active }) => (active ? '#6366f1' : 'transparent')};
    display: flex;
    justify-content: space-between;

    &:hover {
        background: rgba(99, 102, 241, 0.15);
        color: #fff;
        padding-left: 14px;
    }
`

const ChevronIcon = styled.div`
    transform: ${({ isOpen }) => isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
    transition: transform 0.3s ease;
    display: flex;
    align-items: center;
    opacity: 0.7;
`

const RESOLUTIONS = [
    { height: 1080, aspect: 16 / 9, stretched: true, label: 'Native (1080p)', isNative: true },
    { height: 960, aspect: 4 / 3, stretched: true, label: '1280x960 (4:3 Stretched)' },
    { height: 960, aspect: 4 / 3, stretched: false, label: '1280x960 (4:3)' },
    { height: 768, aspect: 4 / 3, stretched: true, label: '1024x768 (4:3 Stretched)' },
    { height: 768, aspect: 4 / 3, stretched: false, label: '1024x768 (4:3)' },
    { height: 600, aspect: 4 / 3, stretched: true, label: '800x600 (4:3 Stretched)' },
    { height: 600, aspect: 4 / 3, stretched: false, label: '800x600 (4:3)' }
]

const FloatingAspectWrapper = styled.div`
    position: absolute;
    top: 14px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    justify-content: center;
    z-index: 40;
`


const FloatingBgColors = styled.div`
    position: absolute;
    top: 15px;
    right: 15px;
    display: flex;
    flex-direction: row;
    gap: 8px;
    z-index: 30;
`

const BgColorBtn = styled.div`
    width: 18px;
    height: 18px;
    border-radius: 3px;
    background: ${({ color }) => color};
    cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
    border: 1px solid
        ${({ active }) => (active ? '#fff' : 'rgba(255,255,255,0.2)')};
    transition: all 0.2s;
    opacity: ${({ $disabled }) => ($disabled ? 0.3 : 1)};
    pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};

    &:hover {
        transform: scale(1.2);
        border-color: #fff;
    }
`

const FloatingColorWrapper = styled.div`
    position: relative;
    pointer-events: auto;
    z-index: 30;
    display: flex;
    align-items: center;

    input[type='color'] {
        position: absolute;
        opacity: 0;
        width: 100%;
        height: 100%;
        cursor: pointer;
        z-index: 2;
    }
`

const PreviewBottomBar = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 50px;
    background: rgba(0, 0, 0, 0.4);
    backdrop-filter: blur(10px);
    display: flex;
    align-items: center;
    padding: 0 15px;
    gap: 6px;
    z-index: 60;
    border-top: 1px solid rgba(255, 255, 255, 0.05);
`

const ColorPresets = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
    opacity: 0.6;
`

const ColorPresetBtn = styled.div`
    width: 20px;
    height: 20px;
    border-radius: 4px;
    background: ${({ $color }) => $color};
    cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
    border: 1px solid rgba(255, 255, 255, 0.2);
    transition: all 0.2s;
    opacity: ${({ $disabled }) => ($disabled ? 0.3 : 1)};

    &:hover {
        transform: scale(1.15);
        border-color: #fff;
        box-shadow: 0 0 10px ${({ $color }) => $color};
    }
`

const TestButton = styled.div`
    height: 24px;
    padding: 0 12px;
    border-radius: 6px;
    //background: linear-gradient(135deg, #10b981 0%, #059669 100%);
    //background: linear-gradient(135deg, #232323 0%, #870000 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    color: #fff;
    font-size: 0.65rem;
    font-weight: 800;
    cursor: pointer;
    border: 1px solid rgba(255, 255, 255, 0.1);
    letter-spacing: 0.5px;
    transition: all 0.2s;
    box-shadow: 0 2px 5px rgba(0,0,0,0.2);
    white-space: nowrap;

    &:hover {
        transform: translateY(-1px);
        border-color: rgba(99, 102, 241, 0.4);
        box-shadow: 0 4px 12px rgba(99, 102, 241, 0.2);
        filter: brightness(1.2);
    }

    &:active {
        transform: translateY(0);
    }
`

const CustomColorBtn = styled.div`
    height: 20px;
    padding: 0 10px;
    border-radius: 6px;
    background: linear-gradient(45deg, #ff0000, #ff00ff, #0000ff, #00ffff, #00ff00, #ffff00, #ff0000);
    background-size: 200% 200%;
    animation: anime-gradient 5s ease infinite;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-size: 0.65rem;
    font-weight: 800;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
    cursor: pointer;
    border: 1px solid rgba(255, 255, 255, 0.2);
    letter-spacing: 0.5px;
    transition: all 0.2s;
    opacity: 0.6;
    min-width: 60px;

    &:hover {
        transform: translateY(-1px);
        border-color: #fff;
    }

    @keyframes anime-gradient {
        0% { background-position: 0% 50% }
        50% { background-position: 100% 50% }
        100% { background-position: 0% 50% }
    }
`

const FloatingAlphaWrapper = styled.div`
    flex-grow: 1;
    height: 20px;
    pointer-events: auto;
    display: flex;
    align-items: center;
    z-index: 30;

    input[type='range'] {
        -webkit-appearance: none;
        width: 100%;
        height: 4px;
        border-radius: 2px;
        background: rgba(255, 255, 255, 0.1);
        outline: none;
        cursor: pointer;

        &::-webkit-slider-thumb {
            -webkit-appearance: none;
            width: 12px;
            height: 12px;
            border-radius: 50%;
            background: #fff;
            box-shadow: 0 0 10px rgba(0, 0, 0, 0.5);
            transition: transform 0.2s;
        }

        &:hover::-webkit-slider-thumb {
            transform: scale(1.2);
            background: #6366f1;
        }
    }
`

const RightPanel = styled.div`
    flex-grow: 1;
    min-width: 310px;
`

const BottomPanel = styled.div`
    width: 100%;
    display: flex;
    justify-content: flex-start;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    margin-top: 20px;
`

const TextArea = styled.textarea`
    width: 100%;
    max-width: 700px;
    height: 95px;
    color: rgba(255, 255, 255, 0.6);
    background-color: rgba(0, 0, 0, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    padding: 16px;
    resize: none;
    font-family: monospace;
    font-size: 0.8rem;
    transition: border 0.2s;

    &:focus {
        outline: none;
        border-color: #6366f1;
        color: #fff;
    }
`

const CopyHint = styled.small`
    opacity: 0.4;
    font-size: 0.75rem;
`

const PresetsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    justify-content: flex-start;
    width: 100%;
    margin-top: 5px;
`

const PresetBtn = styled.button`
    background: ${({ active }) =>
        active ? 'rgba(99, 102, 241, 0.1)' : 'rgba(255, 255, 255, 0.03)'
    };
    border: 1px solid
        ${({ active }) => (active ? '#6366f1' : 'rgba(255, 255, 255, 0.1)')};
    color: ${({ active }) => (active ? '#fff' : 'rgba(255, 255, 255, 0.4)')};
    border-radius: 4px;
    padding: 6px 14px;
    font-size: 0.7rem;
    cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
    transition: all 0.2s;
    font-weight: 400;
    //text-transform: uppercase;
    font-family: var(--font-roboto);
    opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
    pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};

    &:hover {
        background: rgba(255, 255, 255, 0.08);
        color: white;
        border-color: ${({ active }) =>
        active ? '#6366f1' : 'rgba(255, 255, 255, 0.3)'
    };
    }
`

// ------- HELPER FUNCTIONS -------
const calculatePixelSize = (size) => {
    let value = size
    let extraSize = 0
    if (value > 2 && value < 6) extraSize += 1
    else if (value >= 6 && value <= 12) extraSize += 2
    else if (value > 12 && value < 14) extraSize += 3
    else if (value >= 14 && value <= 18) extraSize += 4
    else if (value > 18 && value < 22) extraSize += 5
    else if (value >= 22) extraSize += 6
    return { value: value * 2 + extraSize, extra: extraSize }
}

const calculateTickness = (tickness) => {
    let value = tickness
    let extra = 0
    if (value > 2 && value < 6) extra += 1
    else if (value >= 6 && value <= 12) extra += 2
    else if (value > 12 && value < 14) extra += 3
    else if (value >= 14 && value <= 18) extra += 4
    else if (value > 18 && value < 22) extra += 5
    else if (value >= 22) extra += 6
    return { value: value * 2 + extra, extra: extra }
}

const calculateGap = (gap, thickness) => {
    return gap + calculateTickness(thickness).value / 2 + 4
}

const calculateOutlineThickness = (active, tickness) => {
    if (!active) return 0
    if (tickness == 0.5) return 0.5
    return Math.ceil(tickness)
}

const rgbToHex = (r, g, b) => {
    const toHex = (c) => {
        const h = Math.round(c).toString(16)
        return h.length === 1 ? '0' + h : h
    }
    return '#' + toHex(r) + toHex(g) + toHex(b)
}

const hexToRgb = (hex) => {
    const res = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return res
        ? {
            r: parseInt(res[1], 16),
            g: parseInt(res[2], 16),
            b: parseInt(res[3], 16),
        }
        : null
}

const BG_COLORS = [
    '#2222225e',
    '#e1d6a645',
    '#6a75504e',
    '#5d71854f',
    '#ffffff4e',
]

const COLOR_MAP = {
    0: { r: 255, g: 0, b: 0 },
    1: { r: 0, g: 255, b: 0 },
    2: { r: 255, g: 255, b: 0 },
    3: { r: 0, g: 0, b: 255 },
    4: { r: 0, g: 255, b: 255 },
    5: { r: 255, g: 255, b: 255 },
}

const CROSSHAIR_PRESETS = [
    { name: 's1mple', cmd: 'cl_crosshair_drawoutline 1; cl_crosshair_outlinethickness 1; cl_crosshairalpha 255; cl_crosshaircolor 4; cl_crosshairdot 0; cl_crosshairgap -3; cl_crosshairsize 1; cl_crosshairstyle 4; cl_crosshairthickness 1;' },
    { name: 'ZywOo', cmd: 'cl_crosshair_drawoutline 0; cl_crosshairalpha 255; cl_crosshaircolor 1; cl_crosshairdot 0; cl_crosshairgap -3; cl_crosshairsize 1.5; cl_crosshairstyle 4; cl_crosshairthickness 0.5;' },
    { name: 'NiKo', cmd: 'cl_crosshair_drawoutline 0; cl_crosshairalpha 255; cl_crosshaircolor 5; cl_crosshairdot 0; cl_crosshairgap -4; cl_crosshairsize 1; cl_crosshairstyle 4; cl_crosshairthickness 1;' },
    { name: 'm0NESY', cmd: 'cl_crosshair_drawoutline 0; cl_crosshairalpha 255; cl_crosshaircolor 1; cl_crosshairdot 0; cl_crosshairgap -3; cl_crosshairsize 1.5; cl_crosshairstyle 5; cl_crosshairthickness 0.5;' },
    { name: 'donk', cmd: 'cl_crosshair_drawoutline 0; cl_crosshairalpha 255; cl_crosshaircolor 1; cl_crosshairdot 0; cl_crosshairgap -4; cl_crosshairsize 1; cl_crosshairstyle 4; cl_crosshairthickness 1;' },
    { name: 'ropz', cmd: 'cl_crosshair_drawoutline 0; cl_crosshairalpha 255; cl_crosshaircolor 1; cl_crosshairdot 0; cl_crosshairgap -3; cl_crosshairsize 2; cl_crosshairstyle 4; cl_crosshairthickness 0.5;' },
    { name: 'Twistzz', cmd: 'cl_crosshair_drawoutline 1; cl_crosshair_outlinethickness 1; cl_crosshairalpha 255; cl_crosshaircolor 1; cl_crosshairdot 0; cl_crosshairgap -2; cl_crosshairsize 1.5; cl_crosshairstyle 4; cl_crosshairthickness 0.5;' },
    { name: 'broky', cmd: 'cl_crosshair_drawoutline 1; cl_crosshair_outlinethickness 1; cl_crosshairalpha 255; cl_crosshaircolor 1; cl_crosshairdot 0; cl_crosshairgap -3; cl_crosshairsize 2.5; cl_crosshairstyle 4; cl_crosshairthickness 0.5;' },
    { name: 'dev1ce', cmd: 'cl_crosshair_drawoutline 1; cl_crosshair_outlinethickness 1; cl_crosshairalpha 255; cl_crosshaircolor 1; cl_crosshairdot 0; cl_crosshairgap -2; cl_crosshairsize 2; cl_crosshairstyle 5; cl_crosshairthickness 1;' },
    { name: 'rain', cmd: 'cl_crosshair_drawoutline 0; cl_crosshairalpha 255; cl_crosshaircolor 1; cl_crosshairdot 0; cl_crosshairgap -3; cl_crosshairsize 2; cl_crosshairstyle 5; cl_crosshairthickness 0.5;' },
    { name: 'EliGE', cmd: 'cl_crosshair_drawoutline 1; cl_crosshair_outlinethickness 0.5; cl_crosshairalpha 255; cl_crosshaircolor 5; cl_crosshairdot 1; cl_crosshairgap -4; cl_crosshairsize 1.5; cl_crosshairstyle 4; cl_crosshairthickness 0.5;' },
    { name: 'Hunter', cmd: 'cl_crosshair_drawoutline 0; cl_crosshairalpha 255; cl_crosshaircolor 1; cl_crosshairdot 0; cl_crosshairgap -2; cl_crosshairsize 2; cl_crosshairstyle 4; cl_crosshairthickness 0.5;' },
]

const SPECIAL_CROSSHAIRS = [
    { name: 'Blocky Dot', cmd: 'cl_crosshairstyle 4; cl_crosshairsize 0; cl_crosshairthickness 3; cl_crosshairgap -3; cl_crosshairdot 0; cl_crosshaircolor_r 0; cl_crosshaircolor_g 255; cl_crosshaircolor_b 0; cl_crosshairalpha 200; cl_crosshair_drawoutline 1; cl_crosshair_outlinethickness 1; cl_crosshair_recoil 0; cl_crosshair_t 0; cl_crosshair_friendly_warning 1;' },
    { name: 'Small Square', cmd: 'cl_crosshairstyle 4; cl_crosshairsize 0.5; cl_crosshairthickness 2; cl_crosshairgap -4.5; cl_crosshairdot 0; cl_crosshaircolor 5; cl_crosshair_drawoutline 1; cl_crosshair_outlinethickness 0.5;' },
    { name: 'Boxing', cmd: 'cl_crosshairstyle 4; cl_crosshairsize 1; cl_crosshairthickness 5; cl_crosshairgap -5; cl_crosshairdot 0; cl_crosshaircolor 2; cl_crosshair_drawoutline 1; cl_crosshair_outlinethickness 1.5;' },
    { name: 'Sniper Dot', cmd: 'cl_crosshairstyle 4; cl_crosshairsize 0; cl_crosshairthickness 0.5; cl_crosshairgap 0; cl_crosshairdot 1; cl_crosshaircolor 4; cl_crosshair_drawoutline 1; cl_crosshair_outlinethickness 1; cl_crosshairalpha 255;' },
]

const DotIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="12" cy="12" r="4" fill="currentColor" />
        <path d="M12 4V7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.6" />
        <path d="M12 17V20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.6" />
        <path d="M4 12H7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.6" />
        <path d="M17 12H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.6" />
    </svg>
)

const OutlineIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="5" y="5" width="14" height="14" rx="3" stroke="currentColor" strokeWidth="2.5" />
    </svg>
)

const RandomIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="16 3 21 3 21 8"></polyline>
        <line x1="4" y1="20" x2="21" y2="3"></line>
        <polyline points="21 16 21 21 16 21"></polyline>
        <line x1="15" y1="15" x2="21" y2="21"></line>
        <line x1="4" y1="4" x2="9" y2="9"></line>
    </svg>
)

const ProIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
    </svg>
)

const ChevronLeftIcon = () => (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18 4l-12 8 12 8V4z" />
    </svg>
)

const ChevronRightIcon = () => (
    <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
        <path d="M6 4l12 8-12 8V4z" />
    </svg>
)

// ------- MAIN COMPONENT -------
const CrosshairCanvas = () => {
    const canvasRef = useRef(null)
    const { configData, updateConfig, updateConfigBatch, configIsLocked } = useConfig()
    const { t } = useLanguage()
    const { showPrompt, showConfirm } = useUI()
    const ch = configData.crosshair
    const presets = ch.presets || []
    const toggleBind = ch.bind_toggle_presets || ''

    const [bgColor, setBgColor] = useState(BG_COLORS[0])
    const [tempZoom, setTempZoom] = useState(false)
    const [fixedZoom, setFixedZoom] = useState(1)

    // Resolution Simulation State
    const [simRes, setSimRes] = useState(RESOLUTIONS[0])
    const [isResDropdownOpen, setIsResDropdownOpen] = useState(false)

    const [activeTab, setActiveTab] = useState('gallery')
    const [galleryPage, setGalleryPage] = useState(0)
    const [searchTerm, setSearchTerm] = useState('')
    const [galleryMode, setGalleryMode] = useState('pro') // 'pro', 'recommended', 'random'
    const [randomGallery, setRandomGallery] = useState([])

    // Import states
    const [shareCodeInput, setShareCodeInput] = useState('')
    const [cfgInput, setCfgInput] = useState('')

    // Local states for color picker performance
    const [localColorR, setLocalColorR] = useState(ch.cl_crosshaircolor_r)
    const [localColorG, setLocalColorG] = useState(ch.cl_crosshaircolor_g)
    const [localColorB, setLocalColorB] = useState(ch.cl_crosshaircolor_b)

    // Color picker state
    const [isColorPickerOpen, setIsColorPickerOpen] = useState(false)

    // Ref to always hold latest pending color values — avoids stale closure in closeColorPicker
    const pendingColorRef = useRef({ r: ch.cl_crosshaircolor_r, g: ch.cl_crosshaircolor_g, b: ch.cl_crosshaircolor_b })

    // Renaming state
    const [editingPresetId, setEditingPresetId] = useState(null)
    const [editingName, setEditingName] = useState('')
    const [deletingId, setDeletingId] = useState(null)


    // Sync local color when global color changes (e.g. via presets)
    useEffect(() => {
        setLocalColorR(ch.cl_crosshaircolor_r)
        setLocalColorG(ch.cl_crosshaircolor_g)
        setLocalColorB(ch.cl_crosshaircolor_b)
        pendingColorRef.current = { r: ch.cl_crosshaircolor_r, g: ch.cl_crosshaircolor_g, b: ch.cl_crosshaircolor_b }
    }, [ch.cl_crosshaircolor_r, ch.cl_crosshaircolor_g, ch.cl_crosshaircolor_b])

    const generateRandomCrosshairs = () => {
        const getRandomInSteps = (min, max, step) => {
            const steps = Math.floor((max - min) / step);
            return (min + Math.floor(Math.random() * (steps + 1)) * step);
        };

        const newMiras = [];
        for (let i = 0; i < 12; i++) {
            const size = getRandomInSteps(0.5, 3, 0.5);
            const thick = getRandomInSteps(0.5, 2, 0.5);
            const gap = Math.floor(Math.random() * 10 - 5); // -5 to 4
            const outline = Math.random() > 0.5 ? 1 : 0;
            const outlineThickness = outline ? getRandomInSteps(0.5, 2, 0.5) : 0;
            const r = Math.floor(Math.random() * 256);
            const g = Math.floor(Math.random() * 256);
            const b = Math.floor(Math.random() * 256);
            const dot = Math.random() > 0.85 ? 1 : 0;
            const tStyle = Math.random() > 0.85 ? 1 : 0;
            const style = Math.random() > 0.3 ? 4 : 5; // 70% static, 30% dynamic
            const recoil = Math.random() > 0.75 ? 1 : 0; // 25% chance

            const cmd = `cl_crosshair_drawoutline ${outline}; cl_crosshair_outlinethickness ${outlineThickness}; cl_crosshairalpha 255; cl_crosshaircolor 5; cl_crosshaircolor_r ${r}; cl_crosshaircolor_g ${g}; cl_crosshaircolor_b ${b}; cl_crosshairdot ${dot}; cl_crosshairgap ${gap}; cl_crosshairsize ${size}; cl_crosshairstyle ${style}; cl_crosshairthickness ${thick}; cl_crosshair_t ${tStyle}; cl_crosshair_recoil ${recoil}; `;

            newMiras.push({
                id: `random - ${Date.now()} -${i} `,
                name: `Random ${i + 1} `,
                player: 'Random',
                commands: cmd
            });
        }
        setRandomGallery(newMiras);
        setGalleryMode('random');
        setGalleryPage(0);
        setSearchTerm('');
    };

    const currentScale = tempZoom ? 3 : fixedZoom

    // Base logical size for the preview box (reference for 1080p density)
    const BASE_INTERNAL_HEIGHT = 600

    const size = ch.cl_crosshairsize
    const thickness = ch.cl_crosshairthickness
    const gap = ch.cl_crosshairgap
    const colorR = ch.cl_crosshaircolor_r
    const colorG = ch.cl_crosshaircolor_g
    const colorB = ch.cl_crosshaircolor_b
    const alpha = ch.cl_crosshairalpha
    const dot = ch.cl_crosshairdot === 1 || ch.cl_crosshairdot === true
    const outline =
        ch.cl_crosshair_drawoutline === 1 ||
        ch.cl_crosshair_drawoutline === true
    const outlineThickness = ch.cl_crosshair_outlinethickness
    const recoil =
        ch.cl_crosshair_recoil === 1 || ch.cl_crosshair_recoil === true
    const isTStyle = ch.cl_crosshair_t === 1 || ch.cl_crosshair_t === true
    const style = ch.cl_crosshairstyle
    const friendlyWarning = ch.cl_crosshair_friendly_warning || 0
    const sniperWidth = ch.cl_crosshair_sniper_width || 1

    // Derived synchronously from configData — no extra render cycle
    const cleanCommandString = useMemo(() => {
        const cmds = [
            `cl_crosshairsize ${size}`,
            `cl_crosshairthickness ${thickness}`,
            `cl_crosshairgap ${gap}`,
            `cl_crosshairalpha ${alpha}`,
            `cl_crosshaircolor 5`,
            `cl_crosshaircolor_r ${colorR}`,
            `cl_crosshaircolor_g ${colorG}`,
            `cl_crosshaircolor_b ${colorB}`,
            `cl_crosshairdot ${dot ? 1 : 0}`,
            `cl_crosshair_drawoutline ${outline ? 1 : 0}`,
            `cl_crosshair_outlinethickness ${outlineThickness}`,
            `cl_crosshair_recoil ${recoil ? 1 : 0}`,
            `cl_crosshair_t ${isTStyle ? 1 : 0}`,
            `cl_crosshair_friendly_warning ${friendlyWarning}`,
            `cl_crosshairstyle ${style}`,
            `cl_crosshair_sniper_width ${sniperWidth}`,
        ]
        return cmds.join('; ') + ';'
    }, [size, thickness, gap, alpha, colorR, colorG, colorB, dot, outline, outlineThickness, recoil, isTStyle, style, friendlyWarning, sniperWidth])
    const commandString = cleanCommandString

    // Use ref instead of state for animation to prevent re-renders of the whole component
    const dynamicOffsetRef = useRef(0)
    const animationRef = useRef(null)
    const recoilPosRef = useRef({ x: 0, y: 0 })

    // Helper to get current canvas context and dimensions
    const getCanvasContext = () => {
        const canvas = canvasRef.current
        if (!canvas) return null
        const ctx = canvas.getContext('2d')
        const dpr = window.devicePixelRatio || 1

        // Return current logical dimensions without resizing
        // Resizing clears the canvas and causes flickering if done in a loop/hover
        const width = canvas.width / dpr
        const height = canvas.height / dpr

        return { ctx, width, height }
    }

    // Simulation Helper
    const applySimulationTransform = (ctx, w, h) => {
        if (!simRes.isNative) {
            const baseScale = 1080 / simRes.height
            let scaleX = baseScale
            let scaleY = baseScale

            if (simRes.stretched) {
                scaleX = baseScale * (16 / 9) / (simRes.aspect)
            }

            const cx = w / 2
            const cy = h / 2

            ctx.translate(cx, cy)
            ctx.scale(scaleX, scaleY)
            ctx.translate(-cx, -cy)
        }
    }

    // Animation loop for Dynamic Style and/or Follow Recoil
    useEffect(() => {
        const isDynamic = style === 5
        const hasRecoil = !!recoil

        if (!isDynamic && !hasRecoil) {
            dynamicOffsetRef.current = 0
            recoilPosRef.current = { x: 0, y: 0 }
            if (animationRef.current) cancelAnimationFrame(animationRef.current)

            // Trigger one last draw to reset to 0 with correct transforms
            const info = getCanvasContext()
            if (info) {
                const { ctx, width, height } = info
                ctx.save()
                applySimulationTransform(ctx, width, height)
                drawCrosshair(ctx, width, height)
                ctx.restore()
            }
            return
        }

        let startTime = Date.now()

        const animate = () => {
            const now = Date.now()
            const time = now - startTime

            // --- Dynamic style: gap expansion pulses ---
            if (isDynamic) {
                const cycleDuration = 2200
                const t = time % cycleDuration

                let currentRecoil = 0
                const baseDynamic = 5

                const addPulse = (start, attack, decay, magnitude) => {
                    if (t < start) return 0
                    const localT = t - start
                    if (localT < attack) return (localT / attack) * magnitude
                    if (localT < attack + decay) {
                        const progress = (localT - attack) / decay
                        return magnitude * Math.cos((progress * Math.PI) / 2)
                    }
                    return 0
                }

                currentRecoil += addPulse(100, 30, 150, 8)
                currentRecoil += addPulse(250, 30, 200, 14)
                currentRecoil += addPulse(380, 20, 1200, 25)

                dynamicOffsetRef.current = baseDynamic + currentRecoil
            } else {
                dynamicOffsetRef.current = 0
            }

            // --- Follow recoil: crosshair position drifts upward with jitter ---
            if (hasRecoil) {
                const CYCLE = 2000
                const BURST_END = 500
                const RETURN_END = 950
                const t = time % CYCLE

                if (t <= BURST_END) {
                    // 5 shots, one every 100ms — crosshair rises and jitters
                    const shotNum = Math.min(Math.floor(t / 100), 4)
                    const localT = t % 100
                    const ySteps = [0, 5, 10.5, 15.5, 19, 22]
                    const xSteps = [0, 2.5, -3, 2, -2.5, 3]
                    const yFrom = ySteps[shotNum]
                    const yTo = ySteps[shotNum + 1]
                    const xFrom = xSteps[shotNum]
                    const xTo = xSteps[shotNum + 1]
                    const eased = 1 - Math.pow(1 - localT / 100, 2)
                    recoilPosRef.current = {
                        x: xFrom + (xTo - xFrom) * eased,
                        y: -(yFrom + (yTo - yFrom) * eased),
                    }
                } else if (t <= RETURN_END) {
                    // Crosshair returns smoothly to center
                    const returnProgress = (t - BURST_END) / (RETURN_END - BURST_END)
                    const eased = 1 - Math.pow(1 - returnProgress, 3)
                    recoilPosRef.current = {
                        x: 3 * (1 - eased),
                        y: -22 * (1 - eased),
                    }
                } else {
                    recoilPosRef.current = { x: 0, y: 0 }
                }
            } else {
                recoilPosRef.current = { x: 0, y: 0 }
            }

            // Imperatively draw the frame without React render
            const info = getCanvasContext()
            if (info) {
                const { ctx, width, height } = info
                ctx.save()
                applySimulationTransform(ctx, width, height)
                drawCrosshair(ctx, width, height)
                ctx.restore()
            }

            animationRef.current = requestAnimationFrame(animate)
        }

        animationRef.current = requestAnimationFrame(animate)

        return () => {
            if (animationRef.current) cancelAnimationFrame(animationRef.current)
        }
    }, [style, size, thickness, gap, colorR, colorG, colorB, alpha, dot, outline, outlineThickness, recoil, isTStyle, friendlyWarning, simRes]) // Re-run when props or simRes change

    useEffect(() => {
        const canvas = canvasRef.current
        if (!canvas) return
        const ctx = canvas.getContext('2d')

        // Always match container exact pixels for maximum fidelity
        const rect = canvas.getBoundingClientRect()
        if (rect.width === 0 || rect.height === 0) return

        const w = Math.floor(rect.width)
        const h = Math.floor(rect.height)

        canvas.width = w
        canvas.height = h

        // Save context state before transforms
        ctx.save()

        applySimulationTransform(ctx, w, h)

        ctx.imageSmoothingEnabled = false

        drawCrosshair(ctx, w, h)

        ctx.restore() // Restore for next frame/cleanup
    }, [
        size,
        thickness,
        gap,
        localColorR,
        localColorG,
        localColorB,
        alpha,
        dot,
        outline,
        outlineThickness,
        bgColor,
        recoil,
        isTStyle,
        style,
        friendlyWarning,
        simRes
    ])


    const drawCrosshair = (ctx, w, h) => {
        const centerX = Math.floor(w / 2) + recoilPosRef.current.x
        const centerY = Math.floor(h / 2) + recoilPosRef.current.y

        ctx.clearRect(0, 0, w, h)

        const pixelSize = calculatePixelSize(size).value
        const pixelThickness = calculateTickness(thickness).value
        const pixelOutlineThickness = calculateOutlineThickness(
            outline,
            outlineThickness
        )
        // If Dynamic (style 5), use the animated offset. 
        // If not, offset is 0 as set by the effect.
        // Read directly from ref for animation
        const pixelGap = calculateGap(gap, thickness) + dynamicOffsetRef.current
        const color = `rgba(${localColorR}, ${localColorG}, ${localColorB}, ${alpha / 255})`

        const drawLine = (pos, c, oT) => {
            const totalThick = pixelThickness + 2 * oT
            const totalSz = pixelSize + 2 * oT
            const halfThick = pixelThickness / 2
            ctx.fillStyle = c
            let x, y, width, height
            if (pos === 'left') {
                x = centerX - pixelGap - pixelSize - oT
                y = centerY - halfThick - oT
                width = totalSz
                height = totalThick
            } else if (pos === 'right') {
                x = centerX + pixelGap - oT
                y = centerY - halfThick - oT
                width = totalSz
                height = totalThick
            } else if (pos === 'top') {
                x = centerX - halfThick - oT
                y = centerY - pixelGap - pixelSize - oT
                width = totalThick
                height = totalSz
            } else if (pos === 'bottom') {
                x = centerX - halfThick - oT
                y = centerY + pixelGap - oT
                width = totalThick
                height = totalSz
            }
            ctx.fillRect(
                Math.round(x),
                Math.round(y),
                Math.round(width),
                Math.round(height)
            )
        }

        const dirs = ['left', 'right', 'top', 'bottom']
        const activeDirs = isTStyle ? ['left', 'right', 'bottom'] : dirs

        if (outline && pixelOutlineThickness > 0) {
            const outlineColor = `rgba(0, 0, 0, ${alpha / 255})`
            activeDirs.forEach((d) =>
                drawLine(d, outlineColor, pixelOutlineThickness)
            )
        }
        activeDirs.forEach((d) => drawLine(d, color, 0))

        if (dot) {
            const dotOutSz =
                calculateTickness(thickness).value + pixelOutlineThickness * 2
            const halfOut = Math.floor(dotOutSz / 2)
            if (outline && pixelOutlineThickness > 0) {
                ctx.fillStyle = `rgba(0, 0, 0, ${alpha / 255})`
                ctx.fillRect(
                    centerX - halfOut,
                    centerY - halfOut,
                    dotOutSz,
                    dotOutSz
                )
            }
            const dotSz = calculateTickness(thickness).value
            const half = Math.floor(dotSz / 2)
            ctx.fillStyle = color
            ctx.fillRect(centerX - half, centerY - half, dotSz, dotSz)
        }
    }

    const handleSavePreset = async (e) => {
        if (e) e.stopPropagation();
        if (configIsLocked) return

        if (presets.length >= 12) {
            // Optional: Alert limit reached
            return
        }

        const currentCmds = [
            `cl_crosshairsize ${size} `,
            `cl_crosshairthickness ${thickness} `,
            `cl_crosshairgap ${gap} `,
            `cl_crosshairalpha ${alpha} `,
            `cl_crosshairstyle ${style} `,
            `cl_crosshairdot ${dot ? 1 : 0} `,
            `cl_crosshair_drawoutline ${outline ? 1 : 0} `,
            `cl_crosshair_outlinethickness ${outlineThickness} `,
            `cl_crosshaircolor 5`, // Custom RGB
            `cl_crosshaircolor_r ${colorR}`,
            `cl_crosshaircolor_g ${colorG}`,
            `cl_crosshaircolor_b ${colorB}`,
            `cl_crosshair_recoil ${recoil ? 1 : 0} `,
            `cl_crosshair_t ${isTStyle ? 1 : 0} `,
            `cl_crosshair_friendly_warning ${friendlyWarning} `,
            `cl_crosshair_sniper_width ${sniperWidth} `
        ].join('; ')

        const duplicate = presets.find(p => p.commands === currentCmds)
        if (duplicate) {
            await showConfirm({
                title: t('ATTENTION'),
                message: t('PRESET_ALREADY_EXISTS'),
                confirmText: 'Entendido',
                cancelText: null,
                variant: 'primary'
            })
            return
        }

        // Generate Unique Name
        let counter = 1
        let newName = `Preset ${counter}`
        while (presets.some(p => p.name.trim() === newName)) {
            counter++
            newName = `Preset ${counter}`
        }

        const newPreset = {
            name: newName,
            commands: currentCmds,
            id: Date.now()
        }

        updateConfig('crosshair', 'presets', [...presets, newPreset])
        setActiveTab('saved')
    }

    const handleRemovePreset = async (id) => {
        if (configIsLocked) return

        const confirmed = await showConfirm({
            title: t('DELETE_PRESET_TITLE'),
            message: t('DELETE_PRESET_CONFIRM'),
            variant: 'danger',
            confirmText: t('DELETE_ACTION')
        })

        if (confirmed) {
            setDeletingId(id)
            // Wait for animation
            setTimeout(() => {
                const newPresets = presets.filter(p => p.id !== id)
                updateConfig('crosshair', 'presets', newPresets)
                setDeletingId(null)
            }, 500)
        }
    }

    const handleBindToggleChange = (val) => {
        updateConfig('crosshair', 'bind_toggle_presets', val)
    }

    const startEditing = (preset, e) => {
        if (e) e.stopPropagation()
        setEditingPresetId(preset.id)
        setEditingName(preset.name)
    }

    const cancelEditing = (e) => {
        if (e) e.stopPropagation()
        setEditingPresetId(null)
        setEditingName('')
    }

    const saveEditing = async (e) => {
        if (e) e.stopPropagation()
        const trimmed = editingName.trim()

        if (!trimmed) {
            cancelEditing()
            return
        }

        const duplicate = presets.find(p => p.id !== editingPresetId && p.name.trim().toLowerCase() === trimmed.toLowerCase())
        if (duplicate) {
            await showConfirm({
                title: t('ATTENTION'),
                message: 'Ya existe una mira con ese nombre.',
                confirmText: 'OK',
                variant: 'warning',
                cancelText: null
            })
            return
        }

        const newPresets = presets.map(p => {
            if (p.id === editingPresetId) {
                return { ...p, name: trimmed }
            }
            return p
        })

        updateConfig('crosshair', 'presets', newPresets)
        setEditingPresetId(null)
    }

    const parseCommandString = (str, resetDefaults = false) => {
        const commands = str
            .split(/[;\n]+/)
            .map((c) => c.trim())
            .filter((c) => c)

        let updates = {}
        if (resetDefaults) {
            updates = {
                cl_crosshairsize: 5,
                cl_crosshairthickness: 0.5,
                cl_crosshairgap: 0,
                cl_crosshairalpha: 255,
                cl_crosshaircolor: 1,
                cl_crosshaircolor_r: 0,
                cl_crosshaircolor_g: 255,
                cl_crosshaircolor_b: 0,
                cl_crosshairdot: 0,
                cl_crosshair_drawoutline: 0,
                cl_crosshair_outlinethickness: 0.5,
                cl_crosshairstyle: 4,
                cl_crosshair_recoil: 0,
                cl_crosshair_t: 0,
                cl_crosshair_friendly_warning: 1,
                cl_crosshair_sniper_width: 1
            }
        }

        commands.forEach((cmd) => {
            const cleanCmd = cmd.replace(/"/g, '')

            const [key, ...rest] = cleanCmd.split(' ')
            const val = rest.join(' ')

            if (key === 'cl_crosshairsize') updates[key] = normalizeValue(Math.max(0, parseFloat(val)), 0.5)
            if (key === 'cl_crosshairthickness') {
                const thickness = parseFloat(val)
                updates[key] = normalizeValue(thickness <= 0 ? 0.5 : thickness, 0.5)
            }
            if (key === 'cl_crosshairgap') updates[key] = Math.round(parseFloat(val))
            if (key === 'cl_crosshairalpha') updates[key] = Math.max(0, Math.min(255, parseInt(val)))
            if (key === 'cl_crosshaircolor_r') updates[key] = Math.max(0, Math.min(255, parseInt(val)))
            if (key === 'cl_crosshaircolor_g') updates[key] = Math.max(0, Math.min(255, parseInt(val)))
            if (key === 'cl_crosshaircolor_b') updates[key] = Math.max(0, Math.min(255, parseInt(val)))
            if (key === 'cl_crosshaircolor') {
                const colorIdx = parseInt(val)
                updates['cl_crosshaircolor'] = colorIdx
                // Map all preset indices 0-5 to their RGB defaults.
                // Index 5 maps to white; subsequent cl_crosshaircolor_r/g/b commands will override.
                const c = COLOR_MAP[colorIdx]
                if (c) {
                    updates['cl_crosshaircolor_r'] = c.r
                    updates['cl_crosshaircolor_g'] = c.g
                    updates['cl_crosshaircolor_b'] = c.b
                }
            }
            if (key === 'cl_crosshairdot') updates[key] = (val === '1' || val === 'true' || val === '1 ' ? 1 : 0)
            if (key === 'cl_crosshair_drawoutline') updates[key] = (val === '1' || val === 'true' || val === '1 ' ? 1 : 0)
            if (key === 'cl_crosshair_outlinethickness') updates[key] = normalizeValue(Math.max(0, parseFloat(val)), 0.5)
            if (key === 'cl_crosshair_recoil') updates[key] = (val === '1' || val === 'true' || val === '1 ' ? 1 : 0)
            if (key === 'cl_crosshair_t') updates[key] = (val === '1' || val === 'true' || val === '1 ' ? 1 : 0)
            if (key === 'cl_crosshair_friendly_warning') updates[key] = parseInt(val) || 0
            if (key === 'cl_crosshairstyle') updates[key] = parseInt(val) || 4
            if (key === 'cl_crosshair_sniper_width') updates[key] = parseInt(val) || 1
        })

        if (Object.keys(updates).length > 0) {
            if (updates.cl_crosshaircolor_r !== undefined) {
                pendingColorRef.current.r = updates.cl_crosshaircolor_r
                setLocalColorR(updates.cl_crosshaircolor_r)
            }
            if (updates.cl_crosshaircolor_g !== undefined) {
                pendingColorRef.current.g = updates.cl_crosshaircolor_g
                setLocalColorG(updates.cl_crosshaircolor_g)
            }
            if (updates.cl_crosshaircolor_b !== undefined) {
                pendingColorRef.current.b = updates.cl_crosshaircolor_b
                setLocalColorB(updates.cl_crosshaircolor_b)
            }
            updateConfigBatch('crosshair', updates)
        }
    }

    const handleColorChange = (e) => {
        const rgb = hexToRgb(e.target.value)
        if (rgb) {
            updateConfigBatch('crosshair', {
                cl_crosshaircolor_r: rgb.r,
                cl_crosshaircolor_g: rgb.g,
                cl_crosshaircolor_b: rgb.b
            })
        }
    }

    const handleColorPickerChange = (newHex) => {
        const rgb = hexToRgb(newHex)
        if (rgb) {
            // Always update ref first — ref is always current regardless of render cycle
            pendingColorRef.current = { r: rgb.r, g: rgb.g, b: rgb.b }
            // Update local state for smooth canvas preview
            if (rgb.r !== localColorR || rgb.g !== localColorG || rgb.b !== localColorB) {
                setLocalColorR(rgb.r)
                setLocalColorG(rgb.g)
                setLocalColorB(rgb.b)
            }
        }
    }

    const closeColorPicker = () => {
        setIsColorPickerOpen(false)
        // Use ref to commit — avoids stale closure if close fires in the same batch as the last onChange
        updateConfigBatch('crosshair', {
            cl_crosshaircolor_r: pendingColorRef.current.r,
            cl_crosshaircolor_g: pendingColorRef.current.g,
            cl_crosshaircolor_b: pendingColorRef.current.b
        })
    }

    const toggleColorPicker = () => {
        if (isColorPickerOpen) {
            closeColorPicker()
        } else {
            setIsColorPickerOpen(true)
        }
    }

    const handleAlphaChange = (e) =>
        updateConfig('crosshair', 'cl_crosshairalpha', parseInt(e.target.value))
    const toggleDot = () =>
        updateConfig('crosshair', 'cl_crosshairdot', dot ? 0 : 1)
    const toggleOutline = () => {
        const newVal = outline ? 0 : 1
        const updates = { cl_crosshair_drawoutline: newVal }
        if (newVal === 1 && outlineThickness === 0) {
            updates.cl_crosshair_outlinethickness = 0.5
        }
        updateConfigBatch('crosshair', updates)
    }
    const toggleRecoil = () =>
        updateConfig('crosshair', 'cl_crosshair_recoil', recoil ? 0 : 1)
    const toggleTStyle = () =>
        updateConfig('crosshair', 'cl_crosshair_t', isTStyle ? 0 : 1)
    const setStyle = (s) => updateConfig('crosshair', 'cl_crosshairstyle', s)
    const toggleFriendlyWarning = () =>
        updateConfig(
            'crosshair',
            'cl_crosshair_friendly_warning',
            friendlyWarning === 1 ? 0 : 1
        )

    const handleImportShareCode = (directCode) => {
        if (configIsLocked) return
        const code = directCode || shareCodeInput
        if (!code || !code.trim()) return

        try {
            const crosshairObj = decodeCrosshairShareCode(code.trim())
            const commandStr = crosshairToConVars(crosshairObj)
            track('config_import', { module: 'crosshair', kind: 'sharecode' })
            parseCommandString(commandStr)
            setShareCodeInput('')
        } catch (e) {
            console.error(e)
            showConfirm({
                title: 'Error',
                message: t('INVALID_SHARE_CODE'),
                confirmText: 'OK',
                variant: 'danger',
                cancelText: '' // Hide cancel button
            })
        }
    }

    const handleImportConfig = (directCfg) => {
        if (configIsLocked) return
        const cfgContent = directCfg || cfgInput
        if (!cfgContent || !cfgContent.trim()) return

        try {
            track('config_import', { module: 'crosshair', kind: 'cfg' })
            parseCommandString(cfgContent)
            setCfgInput('')
        } catch (e) {
            console.error(e)
            showConfirm({
                title: 'Error',
                message: 'No se pudieron procesar los comandos del CFG.',
                confirmText: 'OK',
                variant: 'danger',
                cancelText: ''
            })
        }
    }


    const isPresetActive = (cmdString) => {
        if (!cmdString) return false
        const presetCmds = cmdString.split(';').map(c => c.trim()).filter(c => c)

        for (const fullCmd of presetCmds) {
            const parts = fullCmd.split(' ').filter(p => p)
            if (parts.length < 2) continue
            const key = parts[0]
            const val = parts.slice(1).join(' ')
            const currentVal = ch[key]

            // Boolean toggles
            if (['cl_crosshairdot', 'cl_crosshair_drawoutline', 'cl_crosshair_recoil', 'cl_crosshair_t'].includes(key)) {
                const bVal = (val === '1' || val === 'true')
                const bCurrent = (currentVal === 1 || currentVal === true || currentVal === '1')
                if (bVal !== bCurrent) return false
                continue
            }

            // Color Index: Just check if indices match. 
            // The actual RGB values are checked individually (cl_crosshaircolor_r, etc.)
            if (key === 'cl_crosshaircolor') {
                if (parseInt(val) !== parseInt(currentVal)) return false
                continue
            }

            // Numeric check with tolerance
            const numVal = parseFloat(val)
            const numCurrent = parseFloat(currentVal)

            if (!isNaN(numVal) && !isNaN(numCurrent)) {
                if (Math.abs(numVal - numCurrent) > 0.05) return false
            } else {
                // Fallback for non-numeric equal check
                if (String(val) !== String(currentVal)) return false
            }
        }
        return true
    }

    const [showSniperPreview, setShowSniperPreview] = useState(false)
    const previewTimeoutRef = useRef(null)
    const sniperCanvasRef = useRef(null)
    const prevSniperWidth = useRef(sniperWidth)
    const isMountedRef = useRef(false)

    useEffect(() => {
        // Mark as mounted immediately
        if (!isMountedRef.current) {
            isMountedRef.current = true
            prevSniperWidth.current = sniperWidth
            return
        }

        // Compare current with previous to decide if we should show preview
        if (prevSniperWidth.current !== sniperWidth) {

            // Update previous val
            prevSniperWidth.current = sniperWidth

            setShowSniperPreview(true)
            if (previewTimeoutRef.current) clearTimeout(previewTimeoutRef.current)
            previewTimeoutRef.current = setTimeout(() => {
                setShowSniperPreview(false)
            }, 1500)
        }
    }, [sniperWidth])


    // Draw sniper crosshair on canvas for better performance and smoothness
    useEffect(() => {
        const canvas = sniperCanvasRef.current
        if (!canvas) return
        const ctx = canvas.getContext('2d')

        // Settings
        const size = 200 // Canvas size matches container
        const center = size / 2
        const thickness = Math.max(1, sniperWidth)
        const half = thickness / 2

        ctx.clearRect(0, 0, size, size)

        // Draw Violet Cross (No Glow)
        ctx.shadowBlur = 0
        ctx.shadowColor = 'transparent'
        ctx.fillStyle = '#6366f1' // Main violet color

        // Horizontal line
        ctx.fillRect(0, center - half, size, thickness)

        // Vertical line
        ctx.fillRect(center - half, 0, thickness, size)

    }, [sniperWidth])

    return (
        <LayoutContainer>
            <MainRow>
                <PreviewBox>
                    <BackgroundLayer color={bgColor} />
                    <GridLayer />
                    <Canvas
                        ref={canvasRef}
                        $scale={currentScale}
                        $stretched={simRes.stretched}
                        $aspect={simRes.aspect}
                        $isNative={simRes.isNative}
                    />

                    {/* Sniper Width Overlay Preview */}
                    <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '200px',
                        height: '200px',
                        background: 'rgba(0, 0, 0, 0.4)', // Semi-transparent
                        backdropFilter: 'blur(8px)', // Blur effect
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                        opacity: showSniperPreview ? 1 : 0,
                        transition: 'opacity 0.2s ease-in-out',
                        zIndex: 50,
                        border: '3px solid rgba(99, 102, 241, 0.4)', // Violet border
                        boxShadow: '0 0 30px rgba(99, 102, 241, 0.3), inset 0 0 20px rgba(99, 102, 241, 0.1)', // Violet glow
                        overflow: 'hidden' // Ensure canvas stays within circle
                    }}>
                        <canvas
                            ref={sniperCanvasRef}
                            width={200}
                            height={200}
                            style={{ display: 'block' }}
                        />
                    </div>

                    



                    <FloatingAspectWrapper>
                        <Tooltip title={t('SIMULATE_RESOLUTION')} placement="top" arrow>
                            <DropdownContainer>
                                <DropdownHeader onClick={() => !configIsLocked && setIsResDropdownOpen(!isResDropdownOpen)} disabled={configIsLocked} style={{ height: '32px', minWidth: '160px' }}>
                                    <span style={{ fontSize: '0.75rem' }}>{simRes.label}</span>
                                    <ChevronIcon isOpen={isResDropdownOpen}>
                                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M6 9l6 6 6-6" />
                                        </svg>
                                    </ChevronIcon>
                                </DropdownHeader>
                                <DropdownList isOpen={isResDropdownOpen}>
                                    {RESOLUTIONS.map((res, idx) => (
                                        <DropdownItem
                                            key={idx}
                                            active={res.label === simRes.label}
                                            onClick={() => {
                                                setSimRes(res)
                                                setIsResDropdownOpen(false)
                                            }}
                                        >
                                            <span>{res.label}</span>
                                            {res.label === simRes.label && <span style={{ fontSize: '0.8em', color: '#6366f1' }}>●</span>}
                                        </DropdownItem>
                                    ))}
                                </DropdownList>
                            </DropdownContainer>
                        </Tooltip>
                    </FloatingAspectWrapper>

                    {/* Zero-height spacer for layout if needed, or just remove */}

                    <FloatingActions style={{ top: '15px' }}>
                        {/* T-Style and Friendly Warning moved to FloatingTogglesWrapper */}
                        <Tooltip
                            title={style === 5 ? t('DYNAMIC') : t('STATIC')}
                            placement="right"
                            arrow
                        >
                            <StyledIconButton
                                active={style === 5}
                                onClick={() => setStyle(style === 5 ? 4 : 5)}
                                $disabled={configIsLocked}
                            >
                                <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <circle
                                        cx="12"
                                        cy="12"
                                        r="2"
                                        fill="currentColor"
                                        opacity="0.6"
                                    />
                                    <path
                                        d={
                                            style === 5
                                                ? 'M12 5V2 M12 19V22 M5 12H2 M19 12H22'
                                                : 'M12 8V4 M12 16V20 M8 12H4 M16 12H20'
                                        }
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        style={{
                                            transition:
                                                'all 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                        }}
                                    />
                                </svg>
                            </StyledIconButton>
                        </Tooltip>
                        <Tooltip title={t('T_CROSSHAIR')} placement="right" arrow>
                            <ToggleBtn active={isTStyle} onClick={toggleTStyle} $disabled={configIsLocked}>
                                T
                            </ToggleBtn>
                        </Tooltip>
                        <Tooltip title={t('FOLLOW_RECOIL')} placement="right" arrow>
                            <StyledIconButton active={recoil} onClick={toggleRecoil} $disabled={configIsLocked}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                    <path d="M12 20V16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                    <path d="M12 13L10 11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                    <path d="M10 8L13 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                    <circle cx="12" cy="20" r="1.5" fill="currentColor" />
                                </svg>
                            </StyledIconButton>
                        </Tooltip>

                        <Tooltip title={t('CENTER_DOT')} placement="right" arrow>
                            <StyledIconButton active={dot} onClick={toggleDot} $disabled={configIsLocked}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round">
                                    <circle cx="12" cy="12" r="2.5" fill="white" stroke="none" />
                                    <line x1="12" y1="2" x2="12" y2="8" stroke="currentColor" strokeOpacity="0.3" />
                                    <line x1="12" y1="16" x2="12" y2="22" stroke="currentColor" strokeOpacity="0.3" />
                                    <line x1="2" y1="12" x2="8" y2="12" stroke="currentColor" strokeOpacity="0.3" />
                                    <line x1="16" y1="12" x2="22" y2="12" stroke="currentColor" strokeOpacity="0.3" />
                                </svg>
                            </StyledIconButton>
                        </Tooltip>

                        <Tooltip title={t('OUTLINE')} placement="right" arrow>
                            <StyledIconButton active={outline} onClick={toggleOutline} $disabled={configIsLocked}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="5" y="5" width="14" height="14" rx="3" />
                                </svg>
                            </StyledIconButton>
                        </Tooltip>

                        <Tooltip title={t('FRIENDLY_WARNING')} placement="right" arrow>
                            <StyledIconButton active={friendlyWarning === 1 || friendlyWarning === true} onClick={toggleFriendlyWarning} $disabled={configIsLocked}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                    <circle cx="8.5" cy="7" r="4" />
                                    <line x1="18" y1="8" x2="23" y2="13" />
                                    <line x1="23" y1="8" x2="18" y2="13" />
                                </svg>
                            </StyledIconButton>
                        </Tooltip>
                    </FloatingActions>
                    <FloatingBgColors>
                        {BG_COLORS.map((c) => (
                            <BgColorBtn
                                key={c}
                                color={c}
                                active={bgColor === c}
                                onClick={() => setBgColor(c)}
                                $disabled={configIsLocked}
                            />
                        ))}
                    </FloatingBgColors>

                    <PreviewBottomBar>
                        <Tooltip title={t('SAVE_CURRENT_DESC')} arrow>
                            <TestButton
                                onClick={handleSavePreset}
                                disabled={configIsLocked || presets.length >= 12}
                                style={{ opacity: configIsLocked || presets.length >= 12 ? 0.4 : 1 }}
                            >
                                <SaveIcon style={{ fontSize: 16 }} /> {t('SAVE_BTN')}
                            </TestButton>
                        </Tooltip>
                        <div style={{ width: '1px', height: '20px', background: 'rgba(255,255,255,0.1)', marginLeft: '4px', marginRight: '4px' }} />
                        <ColorPresets>
                            {[
                                { name: 'Verde', r: 0, g: 255, b: 0 },
                                { name: 'Amarillo', r: 255, g: 255, b: 0 },
                                { name: 'Azul', r: 0, g: 0, b: 255 },
                                { name: 'Cian', r: 0, g: 255, b: 255 },
                                { name: 'Rojo', r: 255, g: 0, b: 0 },
                                { name: 'Rosa', r: 255, g: 0, b: 255 },
                                { name: 'Blanco', r: 255, g: 255, b: 255 }
                            ].map((cp) => (
                                <Tooltip key={cp.name} title={cp.name} arrow>
                                    <ColorPresetBtn
                                        $color={`rgb(${cp.r}, ${cp.g}, ${cp.b})`}
                                        $disabled={configIsLocked}
                                        onClick={() => {
                                            if (configIsLocked) return
                                            updateConfigBatch('crosshair', {
                                                cl_crosshaircolor: 5, // Added cl_crosshaircolor 5
                                                cl_crosshaircolor_r: cp.r,
                                                cl_crosshaircolor_g: cp.g,
                                                cl_crosshaircolor_b: cp.b
                                            })
                                        }}
                                    />
                                </Tooltip>
                            ))}
                        </ColorPresets>

                        <FloatingColorWrapper style={{ opacity: configIsLocked ? 0.3 : 1, pointerEvents: configIsLocked ? 'none' : 'auto' }}>
                            <CustomColorBtn
                                onClick={toggleColorPicker}
                                style={{ backgroundColor: rgbToHex(localColorR, localColorG, localColorB), color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
                            >
                            </CustomColorBtn>
                        </FloatingColorWrapper>
                        <div style={{ width: '1px', height: '20px', background: 'rgba(255,255,255,0.1)', marginLeft: '4px', marginRight: '4px' }} />

                        <FloatingAlphaWrapper style={{ opacity: configIsLocked ? 0.3 : 1, pointerEvents: configIsLocked ? 'none' : 'auto' }}>
                            <Tooltip title={t('OPACITY')} placement="top" arrow>
                                <input
                                    type="range"
                                    min="0"
                                    max="255"
                                    value={alpha}
                                    onChange={handleAlphaChange}
                                    disabled={configIsLocked}
                                />
                            </Tooltip>
                        </FloatingAlphaWrapper>
                    </PreviewBottomBar>
                </PreviewBox>
                <RightPanel>

                    <CommandInput
                        commandString={cleanCommandString}
                        onChange={parseCommandString}
                        showGroups={[
                            'GENERAL',
                            'OUTLINE_GROUP',
                            'CS2_ENHANCEMENTS',
                        ]}
                        mergeGroups={true}
                        excludeKeys={[
                            'cl_crosshairdot',
                            'cl_crosshair_drawoutline',
                            'cl_crosshair_t',
                            'cl_crosshairstyle',
                        ]}
                        disabled={configIsLocked}
                    />
                </RightPanel>
            </MainRow>
            <BottomPanel>
                <div style={{ width: '100%', maxWidth: '100%', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <TabsHeader style={{ background: 'transparent', gap: '20px', borderBottom: '1px solid rgba(255,255,255,0.05)', borderRadius: 0, width: '100%' }}>
                        <MainTabButton
                            active={activeTab === 'gallery'}
                            onClick={() => setActiveTab('gallery')}
                            disabled={configIsLocked}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="3" width="7" height="7"></rect>
                                <rect x="14" y="3" width="7" height="7"></rect>
                                <rect x="14" y="14" width="7" height="7"></rect>
                                <rect x="3" y="14" width="7" height="7"></rect>
                            </svg>
                            {t('GALLERY')}
                        </MainTabButton>
                        <MainTabButton
                            active={activeTab === 'saved'}
                            onClick={() => setActiveTab('saved')}
                            disabled={configIsLocked}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                        >
                            <BookmarksIcon style={{ fontSize: 16 }} />
                            {t('SAVED')}
                        </MainTabButton>
                        <MainTabButton
                            active={activeTab === 'import'}
                            onClick={() => setActiveTab('import')}
                            disabled={configIsLocked}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                <polyline points="7 10 12 15 17 10"></polyline>
                                <line x1="12" y1="15" x2="12" y2="3"></line>
                            </svg>
                            {t('IMPORT')}
                        </MainTabButton>
                        <MainTabButton
                            active={activeTab === 'export'}
                            onClick={() => setActiveTab('export')}
                            disabled={configIsLocked}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
                                <polyline points="16 6 12 2 8 6"></polyline>
                                <line x1="12" y1="2" x2="12" y2="15"></line>
                            </svg>
                            {t('EXPORT')}
                        </MainTabButton>
                    </TabsHeader>

                    {activeTab === 'saved' && (
                        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: '20px', padding: '10px 0' }}>

                            <div style={{ background: 'rgba(99, 102, 241, 0.1)', border: '1px solid rgba(99, 102, 241, 0.2)', borderRadius: '8px', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '12px', fontSize: '0.8rem', color: '#c7c7ff' }}>
                                <div style={{ background: 'rgba(99, 102, 241, 0.2)', padding: '6px', borderRadius: '50%', display: 'flex' }}>
                                    <span style={{ fontSize: '14px', lineHeight: 1 }}>💡</span>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                    <strong style={{ color: '#fff' }}>{t('MULTI_CROSSHAIR_SYSTEM')}</strong>
                                    <span style={{ opacity: 0.8 }}>{t('MULTI_CROSSHAIR_DESC')}</span>
                                </div>
                            </div>

                            {/* CONTROLS */}
                            <div style={{ display: 'flex', gap: '20px', width: '100%', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(0,0,0,0.2)', padding: '15px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.05)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '25px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <span style={{ fontSize: '0.75rem', fontWeight: '700', color: '#fff' }}>{t('SAVE_CURRENT')}</span>
                                        <span style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.4)', marginTop: '2px' }}>{t('SAVE_CURRENT_DESC')}</span>
                                    </div>
                                    <ActionButton onClick={handleSavePreset} disabled={configIsLocked || presets.length >= 12}>
                                        <SaveIcon style={{ fontSize: 16 }} /> {t('SAVE_BTN')}
                                    </ActionButton>
                                </div>

                                <div style={{ width: '1px', height: '30px', background: 'rgba(255,255,255,0.1)' }} />

                                <div style={{ display: 'flex', alignItems: 'center', gap: '25px' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                        <span style={{ fontSize: '0.75rem', fontWeight: '700', color: '#fff' }}>{t('TOGGLE_BIND')}</span>
                                        <span style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.4)', marginTop: '2px' }}>{t('TOGGLE_BIND_DESC')}</span>
                                    </div>
                                    <div style={{ width: '100px' }}>
                                        <KeySelector value={toggleBind} onChange={handleBindToggleChange} disabled={configIsLocked} fieldPath="crosshair.bind_toggle_presets" />
                                    </div>
                                </div>
                            </div>

                            {/* LIST */}
                            <GalleryGrid>
                                {presets.map((item) => {
                                    const isActive = isPresetActive(item.commands)
                                    const isEditing = editingPresetId === item.id

                                    return (
                                        <GalleryCard
                                            key={item.id}
                                            onClick={() => !isEditing && parseCommandString(item.commands, true)}
                                            disabled={configIsLocked}
                                            active={isActive}
                                            $isDeleting={deletingId === item.id}
                                            style={{
                                                border: isActive ? '1px solid #6366f1' : '1px solid rgba(255, 255, 255, 0.1)',
                                                boxShadow: isActive ? '0 0 0 1px #6366f1, 0 0 15px rgba(99, 102, 241, 0.3)' : 'none'
                                            }}
                                        >
                                            {isActive && (
                                                <div style={{
                                                    position: 'absolute',
                                                    top: '0',
                                                    left: '0',
                                                    background: '#6366f1',
                                                    color: '#fff',
                                                    fontSize: '0.55rem',
                                                    fontWeight: 'bold',
                                                    padding: '2px 6px',
                                                    borderBottomRightRadius: '6px',
                                                    zIndex: 15,
                                                    boxShadow: '2px 2px 5px rgba(0,0,0,0.3)',
                                                    letterSpacing: '0.5px'
                                                }}>
                                                    SELECTED
                                                </div>
                                            )}

                                            <div style={{ position: 'absolute', top: 5, right: 5, zIndex: 10, display: 'flex', gap: '4px' }}>
                                                {!isEditing && (
                                                    <>
                                                        <div
                                                            onClick={(e) => startEditing(item, e)}
                                                            style={{ padding: 4, background: 'rgba(0,0,0,0.5)', borderRadius: 4, display: 'flex', cursor: 'pointer', color: '#fff' }}
                                                            title="Renombrar"
                                                        >
                                                            <EditIcon style={{ fontSize: 14 }} />
                                                        </div>
                                                        <div
                                                            onClick={(e) => { e.stopPropagation(); handleRemovePreset(item.id); }}
                                                            style={{ padding: 4, background: 'rgba(0,0,0,0.5)', borderRadius: 4, display: 'flex', cursor: 'pointer', color: '#ef4444' }}
                                                            title="Eliminar"
                                                        >
                                                            <DeleteIcon style={{ fontSize: 14 }} />
                                                        </div>
                                                    </>
                                                )}
                                            </div>

                                            <div style={{ width: '130px', height: '130px', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <CrosshairMiniPreview commands={item.commands} />
                                            </div>

                                            <GalleryInfo className="gallery-info" style={{ background: isEditing ? 'rgba(0,0,0,0.9)' : undefined }}>
                                                {isEditing ? (
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', width: '100%' }}>
                                                        <input
                                                            autoFocus
                                                            value={editingName}
                                                            onChange={(e) => setEditingName(e.target.value)}
                                                            onClick={(e) => e.stopPropagation()}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') saveEditing(e)
                                                                if (e.key === 'Escape') cancelEditing(e)
                                                            }}
                                                            style={{
                                                                width: '100%',
                                                                background: 'rgba(255,255,255,0.1)',
                                                                border: '1px solid rgba(255,255,255,0.2)',
                                                                color: '#fff',
                                                                fontSize: '0.7rem',
                                                                padding: '2px 4px',
                                                                borderRadius: '4px',
                                                                outline: 'none'
                                                            }}
                                                        />
                                                        <div onClick={saveEditing} style={{ cursor: 'pointer', color: '#4ade80', display: 'flex' }}>
                                                            <CheckIcon style={{ fontSize: 14 }} />
                                                        </div>
                                                        <div onClick={cancelEditing} style={{ cursor: 'pointer', color: '#ef4444', display: 'flex' }}>
                                                            <CloseIcon style={{ fontSize: 14 }} />
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <GalleryName>{item.name}</GalleryName>
                                                )}
                                            </GalleryInfo>
                                        </GalleryCard>
                                    )
                                })}
                                {presets.length === 0 && (
                                    <div style={{ gridColumn: '1 / -1', padding: '40px', textAlign: 'center', color: 'rgba(255,255,255,0.3)', fontSize: '0.8rem', fontStyle: 'italic', background: 'rgba(255,255,255,0.02)', borderRadius: '12px' }}>
                                        {t('NO_SAVED_CROSSHAIRS')}
                                    </div>
                                )}
                            </GalleryGrid>
                        </div>
                    )}

                    {activeTab === 'import' && (
                        <ImportContainer>
                            <ImportCard as="div" style={{ cursor: 'default' }}>
                                <ImportIcon className="import-icon">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                                    </svg>
                                </ImportIcon>
                                <h4>{t('CROSSHAIR_CODE_LABEL')}</h4>
                                <p>{t('PASTE_SHARECODE_DESC')}</p>
                                <ImportInput
                                    placeholder="CSGO-..."
                                    value={shareCodeInput}
                                    onChange={(e) => setShareCodeInput(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleImportShareCode()}
                                />
                                <ImportActionButton
                                    onClick={() => handleImportShareCode()}
                                    disabled={!shareCodeInput.trim()}
                                >
                                    {t('IMPORT')}
                                </ImportActionButton>
                            </ImportCard>

                            <ImportCard as="div" style={{ cursor: 'default' }}>
                                <ImportIcon className="import-icon">
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                        <polyline points="14 2 14 8 20 8"></polyline>
                                        <line x1="16" y1="13" x2="8" y2="13"></line>
                                        <line x1="16" y1="17" x2="8" y2="17"></line>
                                        <polyline points="10 9 9 9 8 9"></polyline>
                                    </svg>
                                </ImportIcon>
                                <h4>{t('CFG_CONSOLE_LABEL')}</h4>
                                <p>{t('PASTE_CROSSHAIR_DESC')}</p>
                                <ImportTextarea
                                    placeholder="cl_crosshairsize 2..."
                                    value={cfgInput}
                                    onChange={(e) => setCfgInput(e.target.value)}
                                />
                                <ImportActionButton
                                    onClick={() => handleImportConfig()}
                                    disabled={!cfgInput.trim()}
                                >
                                    {t('LOAD_COMMANDS')}
                                </ImportActionButton>
                            </ImportCard>
                        </ImportContainer>
                    )}

                    {activeTab === 'export' && (
                        <ExportContainer>
                            <ExportGrid style={{ gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.5fr)' }}>
                                <ExportCard style={{ height: 'fit-content' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <div style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', padding: '8px', borderRadius: '8px' }}>
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path><rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect></svg>
                                        </div>
                                        <h4 style={{ margin: 0, fontFamily: 'var(--font-chakra)', textTransform: 'uppercase', fontSize: '0.9rem' }}>{t('COPY_COMMANDS')}</h4>
                                    </div>
                                    <p style={{ margin: 0, fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', marginTop: '8px' }}>{t('COPY_COMMANDS_DESC')}</p>

                                    <ImportInput
                                        readOnly
                                        value={commandString}
                                        style={{ background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.05)', fontSize: '0.7rem', marginTop: '15px' }}
                                        onClick={(e) => e.target.select()}
                                    />

                                    <ImportActionButton
                                        $variant="success"
                                        style={{ marginTop: '12px' }}
                                        onClick={() => {
                                            navigator.clipboard.writeText(commandString);
                                        }}
                                    >
                                        {t('COPY_TO_CLIPBOARD')}
                                    </ImportActionButton>
                                </ExportCard>

                                <ExportCard style={{ height: 'fit-content' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                        <h4 style={{ margin: 0, fontFamily: 'var(--font-chakra)', textTransform: 'uppercase', fontSize: '0.9rem' }}>{t('CFG_PREVIEW')}</h4>
                                        <span style={{ fontSize: '0.6rem', color: 'rgba(99, 102, 241, 0.8)', background: 'rgba(99, 102, 241, 0.1)', padding: '2px 8px', borderRadius: '4px' }}>{t('READY_TO_PASTE')}</span>
                                    </div>
                                    <ImportTextarea
                                        readOnly
                                        style={{ height: '180px', fontSize: '0.75rem', lineHeight: '1.6', background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.05)', margin: 0 }}
                                        value={commandString.replace(/; /g, '\n')}
                                    />
                                </ExportCard>
                            </ExportGrid>
                        </ExportContainer>
                    )}



                    {activeTab === 'gallery' && (() => {
                        const baseData = galleryMode === 'random' ? randomGallery :
                            (galleryMode === 'recommended' ? RECOMMENDED_GALLERY : CROSSHAIR_GALLERY);
                        const filteredGallery = baseData.filter(item =>
                            item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            item.player.toLowerCase().includes(searchTerm.toLowerCase())
                        );
                        const totalPages = Math.ceil(filteredGallery.length / 12);

                        return (
                            <>
                                <GalleryControls>
                                    <div style={{ display: 'flex', gap: '12px', alignItems: 'center', width: '100%' }}>
                                        <ActionButton
                                            onClick={generateRandomCrosshairs}
                                            title={t('GENERATE_RANDOM_DESC')}
                                        >
                                            <RandomIcon />
                                            {t('GENERATE_RANDOM')}
                                        </ActionButton>

                                        <div style={{ width: '1px', height: '20px', background: 'rgba(255,255,255,0.1)', margin: '0 4px' }} />

                                        <ModeButton
                                            active={galleryMode === 'pro'}
                                            onClick={() => setGalleryMode('pro')}
                                        >
                                            <StarsIcon style={{ fontSize: 16 }} />
                                            {t('PRO_PLAYERS')}
                                        </ModeButton>

                                        <ModeButton
                                            active={galleryMode === 'recommended'}
                                            onClick={() => {
                                                setGalleryMode('recommended');
                                                setGalleryPage(0);
                                            }}
                                            style={{ borderColor: 'rgba(168, 85, 247, 0.4)', color: galleryMode === 'recommended' ? '#a855f7' : 'rgba(255,255,255,0.5)' }}
                                        >
                                            <VerifiedIcon style={{ fontSize: 16 }} />
                                            {t('RECOMMENDED')}
                                        </ModeButton>

                                        {galleryMode === 'pro' && (
                                            <SearchInput
                                                type="text"
                                                placeholder={t('SEARCH_PLACEHOLDER')}
                                                value={searchTerm}
                                                onChange={(e) => {
                                                    setSearchTerm(e.target.value);
                                                    setGalleryPage(0);
                                                }}
                                                style={{ marginLeft: 'auto' }}
                                            />
                                        )}
                                    </div>

                                    {galleryMode !== 'random' && (
                                        <GalleryPagination>
                                            <PaginationCircle
                                                onClick={() => setGalleryPage(p => Math.max(0, p - 1))}
                                                disabled={galleryPage === 0}
                                            >
                                                <ChevronLeftIcon />
                                            </PaginationCircle>
                                            <span style={{ fontSize: '0.75rem', opacity: 0.5, minWidth: '40px', textAlign: 'center', fontFamily: 'var(--font-chakra)', fontWeight: '600' }}>
                                                {totalPages > 0 ? galleryPage + 1 : 0} / {totalPages}
                                            </span>
                                            <PaginationCircle
                                                onClick={() => setGalleryPage(p => Math.min(totalPages - 1, p + 1))}
                                                disabled={galleryPage >= totalPages - 1 || totalPages === 0}
                                            >
                                                <ChevronRightIcon />
                                            </PaginationCircle>
                                        </GalleryPagination>
                                    )}
                                </GalleryControls>

                                <GalleryGrid>
                                    {filteredGallery
                                        .slice(galleryPage * 12, (galleryPage + 1) * 12)
                                        .map((item) => {
                                            let finalCommands = item.commands
                                            if (!finalCommands && item.code) {
                                                try {
                                                    const decoded = decodeCrosshairShareCode(item.code)
                                                    finalCommands = crosshairToConVars(decoded)
                                                } catch (e) {
                                                    console.warn('Failed to decode crosshair for', item.name)
                                                }
                                            }

                                            return (
                                                <GalleryCard
                                                    key={item.id}
                                                    onClick={() => {
                                                        if (!finalCommands) return
                                                        track('preset_use', { kind: 'crosshair', id: item.id })
                                                        parseCommandString(finalCommands, true)
                                                    }}
                                                    disabled={configIsLocked}
                                                    active={finalCommands && isPresetActive(finalCommands)}
                                                >
                                                    <div style={{ width: '130px', height: '130px', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <CrosshairMiniPreview commands={finalCommands || ''} />
                                                    </div>
                                                    <GalleryInfo className="gallery-info">
                                                        <GalleryPlayer>{item.player}</GalleryPlayer>
                                                    </GalleryInfo>
                                                </GalleryCard>
                                            )
                                        })}
                                </GalleryGrid>
                            </>
                        )
                    })()}
                </div>
            </BottomPanel>

            {isColorPickerOpen && (
                <>
                    <PopoverBackdrop onClick={closeColorPicker} />
                    <StyledPopover>
                        <HexColorPicker
                            color={rgbToHex(localColorR, localColorG, localColorB)}
                            onChange={handleColorPickerChange}
                        />
                    </StyledPopover>
                </>
            )}

        </LayoutContainer>
    )
}



export default CrosshairCanvas
